import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;

  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    /**
     * Add click support.
     * This makes parent menu items open their submenu on click.
     */
    this.addEventListener('click', this.activate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('click', this.activate);

    this.#cleanupMutationObserver();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  /**
   * Click behavior:
   * - If the clicked menu item has children/submenu: open submenu container.
   * - If it has no children: allow normal navigation.
   */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    const clickedLink = event.target.closest('[ref="menuitem"]');
    const item = findMenuItem(event.target);

    if (!item || !(item instanceof HTMLElement)) return;

    const submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);
    const isDefaultSlot = item.slot === '';

    /**
     * No submenu = normal link behavior.
     */
    if (!hasSubmenu) {
      return;
    }

    /**
     * Has submenu = stop parent link navigation.
     */
    event.preventDefault();

    this.dispatchEvent(new MegaMenuHoverEvent());

    /**
     * If user clicks the same active parent again, close it.
     */
    if (item === this.#state.activeItem) {
      this.#deactivate();
      return;
    }

    /**
     * Deactivate previous menu item.
     */
    const previousItem = this.#state.activeItem;

    if (previousItem instanceof HTMLElement) {
      const previousLink = previousItem.querySelector('[ref="menuitem"]');
      const previousSubmenu = findSubmenu(previousItem);

      previousItem.ariaExpanded = 'false';

      if (previousLink instanceof HTMLElement) {
        previousLink.setAttribute('aria-expanded', 'false');
      }

      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
        previousSubmenu.style.visibility = '';
        previousSubmenu.style.opacity = '';
        previousSubmenu.style.pointerEvents = '';

        const previousSubmenuInner = previousSubmenu.querySelector('.menu-list__submenu-inner');

        if (previousSubmenuInner instanceof HTMLElement) {
          previousSubmenuInner.style.opacity = '';
          previousSubmenuInner.style.transform = '';
        }
      }
    }

    /**
     * Activate current menu item.
     */
    this.#state.activeItem = item;

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    /**
     * Important:
     * Your CSS checks for [aria-expanded="true"] inside .menu-list__list-item,
     * so we must set aria-expanded on the actual link too.
     */
    if (clickedLink instanceof HTMLElement) {
      clickedLink.setAttribute('aria-expanded', 'true');
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    submenu.dataset.active = '';

    /**
     * Force submenu visible before measuring height.
     */
    submenu.style.visibility = 'visible';
    submenu.style.opacity = '1';
    submenu.style.pointerEvents = 'auto';

    const submenuInner = submenu.querySelector('.menu-list__submenu-inner');

    if (submenuInner instanceof HTMLElement) {
      submenuInner.style.opacity = '1';
      submenuInner.style.transform = 'translateY(0)';
    }

 const finalHeight =
  (submenu.scrollHeight ||
    submenu.offsetHeight ||
    submenuInner?.scrollHeight ||
    submenuInner?.offsetHeight ||
    0);

    this.headerComponent?.style.setProperty('--submenu-height', `${finalHeight}px`);

    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(document.activeElement);

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(event.relatedTarget);

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToSubmenu || isMovingToOverflowMenu) return;

    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    const link = item.querySelector('[ref="menuitem"]');

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';

    if (item instanceof HTMLElement) {
      item.ariaExpanded = 'false';
    }

    if (link instanceof HTMLElement) {
      link.setAttribute('aria-expanded', 'false');
    }

    if (submenu) {
      delete submenu.dataset.active;
      submenu.style.visibility = '';
      submenu.style.opacity = '';
      submenu.style.pointerEvents = '';

      const submenuInner = submenu.querySelector('.menu-list__submenu-inner');

      if (submenuInner instanceof HTMLElement) {
        submenuInner.style.opacity = '';
        submenuInner.style.transform = '';
      }
    }
  };

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute(
      'data-submenu-overlap-bottom-row'
    );

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? this.headerComponent.querySelector('.header__row--top')?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const fullOpenHeaderHeight =
      submenuHeight === 0 ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images.forEach((img) => img.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/* Helpers */

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  /**
   * If clicked directly on the menu link.
   */
  if (element.matches('[ref="menuitem"]')) {
    return element.closest('.menu-list__list-item');
  }

  /**
   * If clicked inside the link, for example:
   * span, badge, title, icon, etc.
   */
  const link = element.closest('[ref="menuitem"]');

  if (link) {
    return link.closest('.menu-list__list-item');
  }

  /**
   * Overflow menu safety.
   */
  if (element.closest('[slot="overflow"]')) {
    const overflowRoot = element
      .closest('overflow-list, .overflow-menu')
      ?.querySelector('[slot="overflow"]');

    return overflowRoot?.closest('.menu-list__list-item') || null;
  }

  return null;
}

function findSubmenu(element) {
  if (!(element instanceof Element)) return null;

  const submenu = element.querySelector(':scope > [ref="submenu[]"]');

  return submenu instanceof HTMLElement ? submenu : null;
}